import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Trash2, Factory, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"
import { DeleteManufacturerComposer } from "@/forms/sensory"
import { useToast } from "@/contexts/ToastContext"

export interface Manufacturer {
  id: string
  manufacturerNumber: number
  manufacturerName: string
}

interface ManufacturerListProps {
  onAdd?: () => void
  onEdit?: (manufacturer: Manufacturer) => void
}

export function ManufacturerList({ onAdd, onEdit }: ManufacturerListProps) {
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedManufacturer, setSelectedManufacturer] = useState<Manufacturer | null>(null)
  const itemsPerPage = 10

  // Fetch manufacturers from API
  const { data, isLoading, isError, refetch } = useApiQuery(
    "getDeviceManufacturers",
    {}
  )

  // Map API response to component state, excluding archived items
  const manufacturers: Manufacturer[] = (data?.content?.manufacturers || [])
    .filter((m: any) => !m.isArchived)
    .map((m: any) => ({
      id: m.id || String(m.manufacturerNumber),
      manufacturerNumber: m.manufacturerNumber,
      manufacturerName: m.manufacturerName,
    }))

  const filteredManufacturers = manufacturers.filter((manufacturer) =>
    manufacturer.manufacturerName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const totalPages = Math.ceil(filteredManufacturers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedManufacturers = filteredManufacturers.slice(startIndex, startIndex + itemsPerPage)

  const handleDelete = (manufacturer: Manufacturer) => {
    setSelectedManufacturer(manufacturer)
    setIsDeleteDialogOpen(true)
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load manufacturers</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Factory className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Manufacturers</CardTitle>
                <CardDescription className="text-base">Manage device manufacturers</CardDescription>
              </div>
            </div>

            <Button size="sm" className="gap-2" onClick={() => onAdd?.()}>
              <Plus className="h-4 w-4" />
              Add Manufacturer
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search manufacturers..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
        {/* Table */}
        <Table className="text-base">
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-[100px] font-semibold text-base">ID</TableHead>
              <TableHead className="font-semibold text-base">Manufacturer Name</TableHead>
              <TableHead className="w-[120px] text-right font-semibold text-base">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedManufacturers.length > 0 ? (
              paginatedManufacturers.map((manufacturer) => (
                <TableRow key={manufacturer.id} className="transition-colors hover:bg-muted/50">
                  <TableCell className="text-base text-muted-foreground">
                    {manufacturer.manufacturerNumber}
                  </TableCell>
                  <TableCell className="font-medium text-base">
                    <Link
                      to="/sensory/manufacturers/$manufacturerNumber"
                      params={{ manufacturerNumber: String(manufacturer.manufacturerNumber) }}
                      className="text-primary hover:underline"
                    >
                      {manufacturer.manufacturerName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                        <Link
                          to="/sensory/manufacturers/$manufacturerNumber/edit"
                          params={{ manufacturerNumber: String(manufacturer.manufacturerNumber) }}
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit {manufacturer.manufacturerName}</span>
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleDelete(manufacturer)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete {manufacturer.manufacturerName}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                  No manufacturers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t py-3">
            <p className="text-base text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + itemsPerPage, filteredManufacturers.length)} of{" "}
              {filteredManufacturers.length} manufacturers
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-base font-medium">Page {currentPage} of {totalPages}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Manufacturer</DialogTitle>
            <DialogDescription>
              Please confirm you want to delete this manufacturer.
            </DialogDescription>
          </DialogHeader>
          {selectedManufacturer && (
            <DeleteManufacturerComposer
              manufacturerNumber={selectedManufacturer.manufacturerNumber}
              manufacturerName={selectedManufacturer.manufacturerName}
              showHeader={false}
              onSuccess={() => {
                setIsDeleteDialogOpen(false)
                setSelectedManufacturer(null)
                refetch()
              }}
              onError={(error) => {
                log.error('Delete manufacturer error in list:', {
                  selectedManufacturer,
                  error,
                })
                // Check if it's a "not found" error - might already be deleted
                if (error.message.includes('does not exist') || error.message.includes('Not Found')) {
                  toast({ title: 'Notice', description: 'This manufacturer has already been deleted or does not exist. Refreshing list...', type: 'warning' })
                  refetch() // Refresh to sync with backend
                } else {
                  toast({ title: 'Error', description: `Failed to delete manufacturer: ${error.message}`, type: 'error' })
                }
                setIsDeleteDialogOpen(false)
                setSelectedManufacturer(null)
              }}
              onCancel={() => {
                setIsDeleteDialogOpen(false)
                setSelectedManufacturer(null)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
